import moment from 'moment';
import classnames from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';
import {fromJS, Map} from 'immutable';
import {callTargetedAction, setUpStore} from 'client/framework';
import {renderMoment} from 'lib/timeUtils';

import {Icon, WithTooltip} from '@albert-io/atomic';
import {StudentModelV2} from 'resources/GeneratedModels/Student/StudentModel.v2';
import {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';
import ButtonDropdown from 'client/generic/ButtonDropdown/ButtonDropdown.react';
import WindowConfirm from 'generic/WindowConfirm/WindowConfirm.react';
import windowConfirmActions from 'generic/WindowConfirm/WindowConfirmActions';

import submissionResetModalActions from './SubmissionResetModal.actions';
import SubmissionResetModalStore from './SubmissionResetModal.store';
import './submission-indicator.scss';

/* SubmissionIndicator & SubmissionResetModal

  * Use this pair of components to indicate and manipulate the submission status of
  * an assignment for a given student.
  *
  * The two are linked via their `windowConfirmStoreName` and `submissionResetModalStoreName`
  * props.  Each of these SubmissionIndicator props must match their counterparts in the
  * SubmissionResetModal!
  *
  * A ButtonDropdown component in the SubmissionIndicator will tee up the selected student
  * and assignment relationship that you want to reset, passing the pair to the
  * SubmissionResetModal store.
  *
  * The WindowConfirm component in the SubmissionResetModal finishes the job.
*/

export class SubmissionIndicator extends React.PureComponent {
  static propTypes = {
    assignment: PropTypes.instanceOf(AssignmentModelV3).isRequired,
    isComplete: PropTypes.bool,
    student: PropTypes.instanceOf(StudentModelV2).isRequired,
    submittedTimestamp: PropTypes.string,
    windowConfirmStoreName: PropTypes.string.isRequired,
    submissionResetModalStoreName: PropTypes.string.isRequired
  };

  handleResetStatus = () => {
    callTargetedAction({
      targetStore: this.props.windowConfirmStoreName,
      name: windowConfirmActions.SHOW_MODAL
    });
  };

  setUpStoreData = () => {
    callTargetedAction({
      targetStore: this.props.submissionResetModalStoreName,
      name: submissionResetModalActions.SET_ACTIVE_ASSIGNMENT,
      payload: this.props.assignment
    });
    callTargetedAction({
      targetStore: this.props.submissionResetModalStoreName,
      name: submissionResetModalActions.SET_ACTIVE_STUDENT,
      payload: this.props.student
    });
  };

  render() {
    const {assignment, isComplete, submittedTimestamp} = this.props;

    let isLate = false;
    if (submittedTimestamp) {
      const parsedSubmittedTimestamp = moment(submittedTimestamp); // use timutil
      isLate = parsedSubmittedTimestamp.isAfter(assignment.getDueDate());
    }

    const badgeClasses = classnames('submission-indicator__status-badge', 'u-mar-r_2', {
      'submission-indicator__status-badge--incomplete': !submittedTimestamp
    });

    const labelClasses = classnames('submission-indicator__status-label', {
      'submission-indicator__status-label--incomplete': !submittedTimestamp,
      'submission-indicator__status-label--late': isLate,
      'submission-indicator__status-label--on-time': submittedTimestamp && !isLate
    });

    const lateStatus = isLate ? 'Submitted Late' : 'Submitted';
    const content = submittedTimestamp ? lateStatus : 'Not Submitted';

    const iconStyle = submittedTimestamp ? 'solid' : 'regular';

    let iconColor;

    if (!submittedTimestamp) {
      iconColor = 'inherit';
    } else if (isLate) {
      iconColor = 'destructive';
    } else {
      iconColor = 'success';
    }

    const dropdown =
      !isComplete && submittedTimestamp && assignment.getSections().isEmpty() ? (
        <ButtonDropdown
          unbutton
          className='submission-indicator__status-dropdown'
          dropdownItems={fromJS([
            {
              displayName: 'Reset status',
              callback: this.handleResetStatus
            }
          ])}
          handleClick={this.setUpStoreData}
        />
      ) : null;

    const tooltipId = submittedTimestamp
      ? `tooltip${this.props.student.getId()}${this.props.assignment.getId()}`
      : null;

    return (
      <div aria-describedby={tooltipId} className='u-display_flex u-align-items_center'>
        <WithTooltip
          as='div'
          content={renderMoment(submittedTimestamp)}
          display='flex'
          enabled={Boolean(submittedTimestamp)}
          focusable
          id={tooltipId}
          propagateHoverEvent
          wrapperProps={{
            className: 'submission-indicator u-align-items_center u-pad_1'
          }}
        >
          <Icon className={badgeClasses} icon='circle' color={iconColor} iconStyle={iconStyle} />
          <span className={labelClasses}>{content}</span>
        </WithTooltip>
        {dropdown}
      </div>
    );
  }
}

export class SubmissionResetModal extends React.Component {
  static propTypes = {
    onReset: PropTypes.func,
    storeName: PropTypes.string.isRequired,
    windowConfirmStoreName: PropTypes.string.isRequired
  };

  static defaultProps = {
    onReset: () => {}
  };

  getStore() {
    return setUpStore(SubmissionResetModalStore, this.props.storeName);
  }

  resetStatus = async () => {
    const assignmentId = this.getStore().getActiveAssignment().getId();
    const updatedStudentModel = this.getStore()
      .getActiveStudent()
      .addRelationship({
        type: 'assignments_v2',
        relation: [assignmentId],
        customMeta: Map().set(assignmentId, {submitted: null})
      });

    await updatedStudentModel.save();
    this.props.onReset();
  };

  onCancel = () => {
    callTargetedAction({
      targetStore: this.props.storeName,
      name: submissionResetModalActions.SET_ACTIVE_STUDENT,
      payload: null
    });
  };

  render() {
    const student = this.getStore().getActiveStudent();
    const assignment = this.getStore().getActiveAssignment();
    if (!student || !assignment) {
      return null;
    }

    let message = `Are you sure you want to reset the submit status for ${student.getFullName()}?`;
    let title = 'Reset submit status';
    if (assignment.isOverDue()) {
      message =
        'Your assignment is past due. If you reset this student’s status, they will not be able to submit or place guesses against the assignment, unless you change the due date as well.';
      title = 'Warning! Assignment is closed';
    } else if (assignment.isPastDue()) {
      message =
        'Your assignment is past due, but allows for late submission. If you reset this student’s status, they will only be able to complete the assignment as “Late” unless you change the due date as well.';
      title = 'Warning! Assignment is past due';
    }

    return (
      <WindowConfirm
        confirmButtonColor='red'
        confirmButtonText='Reset'
        message={message}
        onCancel={this.onCancel}
        onConfirm={this.resetStatus}
        onDismiss={this.onCancel}
        storeName={this.props.windowConfirmStoreName}
        title={title}
      />
    );
  }
}
